"use client";

import { useEffect, useEffectEvent, useMemo, useState } from "react";
import { useHeroMapContext } from "@/components/home/context/hero-map-context";
import {
  DEFAULT_QUANTITY,
  DEFAULT_RADIUS_MILES,
} from "@/components/home/context/search-context";
import { useListingBrowseContext } from "./context/listing-browse-context";
import {
  buildListingSearchQuery,
  parseListingSearchParams,
  type ListingSearchState,
} from "../utils/parse-search-params";

type BrowseContext = ReturnType<typeof useListingBrowseContext>;
type HeroMapContext = ReturnType<typeof useHeroMapContext>;

function applyFilters(browse: BrowseContext, parsed: ListingSearchState) {
  const productType = browse.lockedProductType ?? parsed.productType ?? null;
  if (productType !== browse.productType) browse.setProductType(productType);

  const searchQuery = parsed.searchQuery ?? "";
  if (searchQuery !== browse.searchQuery) browse.setSearchQuery(searchQuery);

  const intentFilter = browse.saleOnly ? "all" : (parsed.intentFilter ?? "all");
  if (intentFilter !== browse.intentFilter) {
    browse.setIntentFilter(intentFilter);
  }

  const conditions = parsed.conditions ?? [];
  if (conditions.join(",") !== browse.conditions.join(",")) {
    browse.setConditions(conditions);
  }

  const min = parsed.priceRange?.min ?? null;
  const max = parsed.priceRange?.max ?? null;
  if (min !== browse.priceRange.min || max !== browse.priceRange.max) {
    browse.setPriceRange({ min, max });
  }

  const stateCode = parsed.stateCode ?? null;
  if (stateCode !== browse.stateCode) browse.setStateCode(stateCode);

  const listedAfterDays = parsed.listedAfterDays ?? null;
  if (listedAfterDays !== browse.listedAfterDays) {
    browse.setListedAfterDays(listedAfterDays);
  }
}

function sameLocation(
  current: HeroMapContext["location"],
  next: ListingSearchState["location"],
): boolean {
  if (!next) return current === null;
  return (
    current?.lat === next.lat &&
    current?.lng === next.lng &&
    current?.postalCode === next.postalCode &&
    current?.city === next.city &&
    current?.state === next.state
  );
}

function applyLocation(heroMap: HeroMapContext, parsed: ListingSearchState) {
  if (!sameLocation(heroMap.location, parsed.location)) {
    heroMap.setLocation(parsed.location ?? null);
  }

  const radiusMiles = parsed.radiusMiles ?? DEFAULT_RADIUS_MILES;
  if (radiusMiles !== heroMap.radiusMiles) heroMap.setRadiusMiles(radiusMiles);

  const quantity = parsed.quantity ?? DEFAULT_QUANTITY;
  if (quantity !== heroMap.quantity) heroMap.setQuantity(quantity);
}

function useDesiredQuery(browse: BrowseContext, heroMap: HeroMapContext) {
  return useMemo(
    () =>
      buildListingSearchQuery({
        location: heroMap.location
          ? {
              lat: heroMap.location.lat,
              lng: heroMap.location.lng,
              postalCode: heroMap.location.postalCode,
              city: heroMap.location.city,
              state: heroMap.location.state,
            }
          : undefined,
        radiusMiles: heroMap.location
          ? (heroMap.radiusMiles ?? undefined)
          : undefined,
        quantity: heroMap.quantity,
        productType: browse.lockedProductType ? undefined : (browse.productType ?? undefined),
        searchQuery: browse.searchQuery || undefined,
        intentFilter: browse.intentFilter,
        conditions: browse.conditions,
        priceRange: browse.priceRange,
        stateCode: browse.stateCode ?? undefined,
        listedAfterDays: browse.listedAfterDays ?? undefined,
      }),
    [browse, heroMap],
  );
}

// WHY: read the query through `window.location` rather than `useSearchParams`. That hook opts its Suspense boundary out of the prerender, and on this page the boundary is every listing, heading and internal link — the built HTML came back with none of them. Nothing in this tree may call it.
export function useListingUrlSync() {
  const browse = useListingBrowseContext();
  const heroMap = useHeroMapContext();
  const [hasReadUrl, setHasReadUrl] = useState(false);
  const desired = useDesiredQuery(browse, heroMap);

  const applyFromUrl = useEffectEvent(() => {
    const parsed = parseListingSearchParams(
      Object.fromEntries(new URLSearchParams(window.location.search).entries()),
    );
    applyFilters(browse, parsed);
    applyLocation(heroMap, parsed);
    setHasReadUrl(true);
  });

  useEffect(() => {
    const onHistoryChange = () => applyFromUrl();
    onHistoryChange();
    window.addEventListener("popstate", onHistoryChange);
    return () => window.removeEventListener("popstate", onHistoryChange);
  }, []);

  useEffect(() => {
    if (!hasReadUrl) return;
    const current = window.location.search.replace(/^\?/, "");
    if (desired === current) return;
    window.history.replaceState(
      null,
      "",
      desired ? `?${desired}` : window.location.pathname,
    );
  }, [hasReadUrl, desired]);
}
